// ----------------------------------------------------------------------

import { applyTemplateVariables, extractParametersFromSendStructure } from './file-upload-gsc';

type Props = {
  currentUserId: string | null;
  conversation: any;
  t?: any;
};

export const profileType = [
  {
    name: 'Variance Whatsapp Business',
    icon: 'hugeicons:whatsapp-business',
    type: 'WHATSAPP',
  },
  {
    name: 'Variance Mail',
    icon: 'material-symbols:mail-outline-rounded',
    type: 'EMAIL',
  },
  {
    name: 'Variance Voice',
    icon: 'ci:user-voice',
    type: 'VOICE',
  },
  {
    name: 'Variance Live Chat',
    icon: 'material-symbols:video-chat-outline',
    type: 'LIVE_CHAT',
  },
];

export const userType = [
  {
    name: 'Variance Whatsapp Business',
    icon: '/assets/icons/navbar/ic-variance-whatsapp-business.svg',
    type: 'WHATSAPP',
  },
  {
    name: 'Variance Mail',
    icon: '/assets/icons/navbar/ic-variance-mail.svg',
    type: 'EMAIL',
  },
  {
    name: 'Variance Voice',
    icon: '/assets/icons/navbar/ic-variance-voice.svg',
    type: 'VOICE',
  },
  {
    name: 'Variance Live Chat',
    icon: '/assets/icons/navbar/ic-variance-livechat.svg',
    type: 'LIVE_CHAT',
  },
];

export const labelData = () => [
  {
    labelName: 'Support',
    labelColor: 'warning',
    labelDescription: 'This is a support ticket',
  },
  {
    labelName: 'Sales',
    labelColor: 'primary',
    labelDescription: 'This is a sales-related ticket',
  },
  {
    labelName: 'Whatsapp',
    labelColor: 'secondary',
    labelDescription: 'This is a WhatsApp-related ticket',
  },
  {
    labelName: 'Billing',
    labelColor: 'error',
    labelDescription: 'This is a billing-related ticket',
  },
  {
    labelName: 'Technical',
    labelColor: 'info',
    labelDescription: 'This is a technical issue ticket',
  },
  {
    labelName: 'Feedback',
    labelColor: 'success',
    labelDescription: 'This is a customer feedback ticket',
  },
  {
    labelName: 'Urgent',
    labelColor: 'error',
    labelDescription: 'This is an urgent issue ticket',
  },
  {
    labelName: 'Feature Request',
    labelColor: 'success',
    labelDescription: 'This is a feature request ticket',
  },
  {
    labelName: 'Bug',
    labelColor: 'error',
    labelDescription: 'This is a bug report ticket',
  },
  {
    labelName: 'General Inquiry',
    labelColor: 'info',
    labelDescription: 'This is a general inquiry ticket',
  },
];

export function getNavItem({ conversation, currentUserId, t }: Props) {
  const { channel, contact, lastMessage, contactId, lastLiveChatMessage, lastEmail } = conversation;

  const participantsInConversation = {
    ...contact,
    iconType: userType.find((user) => user.type === channel?.channelType),
  };

  const group = participantsInConversation.length > 1;

  const displayName =
    participantsInConversation.firstName || participantsInConversation.lastName
      ? `${participantsInConversation.firstName || ''} ${participantsInConversation.lastName || ''}`
      : (channel?.channelType === 'WHATSAPP' && participantsInConversation.phone) ||
        (channel?.channelType === 'EMAIL' && participantsInConversation.email) ||
        (channel?.channelType === 'VOICE' && participantsInConversation.phone) ||
        (channel?.channelType === 'LIVE_CHAT' && participantsInConversation.phone) ||
        'Unknown';

  const hasOnlineInGroup = group ? true : false;

  let displayText = '';
  let status = false;
  if (lastMessage) {
    const sender = lastMessage?.senderId !== contact.id ? `${t('YOU')}:  ` : '';
    if (lastMessage?.contentType === 'template') {
      const parsedSend = JSON.parse(lastMessage?.sendStructure);
      const parsedTemplate = JSON.parse(lastMessage?.templateStructure);
      const parameters = extractParametersFromSendStructure(parsedSend);
      const appliedTemplates = applyTemplateVariables(parameters, parsedTemplate);
      displayText = `${sender}${appliedTemplates?.components[0]?.text || (appliedTemplates?.components[0]?.format && 'Sent a Media') || ''}`;
    } else if (lastMessage.contentType === 'button') {
      displayText = `${sender}${lastMessage.text}`;
    } else {
      const message =
        lastMessage?.contentType !== 'text'
          ? lastMessage.messageType === 'INBOUND'
            ? t('RECEIVE_A_MEDIA')
            : t('SENT_A_MEDIA')
          : lastMessage?.text;
      displayText = `${sender}${message}`;
    }
    status = lastMessage?.senderId === contactId && lastMessage?.status?.toLowerCase() !== 'read';
  }

  if (lastEmail) {
    const sender = lastEmail?.senderId !== contact.id ? `${t('YOU')}:  ` : '';

    const message = lastEmail?.subject ? lastEmail?.subject : '(no subject)';
    displayText = `${sender}${message}`;

    status = lastEmail?.senderId === contactId && lastEmail?.emailType === 'INBOUND';
  }

  if (lastLiveChatMessage) {
    const sender = lastLiveChatMessage?.senderId !== contact.id ? `${t('YOU')}:  ` : '';

    const message =
      lastLiveChatMessage?.contentType !== 'text'
        ? lastLiveChatMessage?.messageType === 'INBOUND'
          ? t('RECEIVE_A_MEDIA')
          : t('SENT_A_MEDIA')
        : lastLiveChatMessage?.text;
    displayText = `${sender}${message}`;

    status =
      lastLiveChatMessage?.senderId === contactId && lastLiveChatMessage?.messageType === 'INBOUND';
  }

  const selectedProfileType = profileType.find((profile) => profile.type === channel.channelType);

  const updateSelectedProfiletype = {
    ...selectedProfileType,
    name: channel.channelName,
  };

  const markdownToHtml = (text: string) => {
    if (!text) return '';
    return text
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      .replace(/~(.*?)~/g, '$1');
  };

  return {
    group,
    displayName,
    displayText: displayText ? markdownToHtml(displayText) : '',
    participants: participantsInConversation,
    lastActivity: conversation?.updatedAt,
    hasOnlineInGroup,
    selectedProfileType: updateSelectedProfiletype,
    messageStatus: status,
  };
}

export function getConversationName({ conversation }: Props) {
  const displayName =
    conversation?.contact?.firstName || conversation?.contact?.lastName
      ? `${conversation?.contact?.firstName || ''} ${conversation?.contact?.lastName || ''}`
      : (conversation?.channel?.channelType === 'WHATSAPP' && conversation?.contact?.phone) ||
        (conversation?.channel?.channelType === 'EMAIL' && conversation?.contact?.email) ||
        (conversation?.channel?.channelType === 'VOICE' && conversation?.contact?.phone) ||
        (conversation?.channel?.channelType === 'LIVE_CHAT' && conversation?.contact?.phone) ||
        'Unknown';

  return displayName || 'Unknow';
}
export function getConversationUserProfile({ conversation }: Props) {
  const userProfile =
    conversation?.contact?.ProfileURL ||
    conversation?.contact?.firstName ||
    conversation?.contact?.lastName;

  return userProfile || '';
}
export function getUserCallDetails({ conversation }: Props) {
  const userPhoneNumber = conversation?.contact?.phone || '';
  const userChannelType = conversation?.channel?.channelType || '';
  return { userPhoneNumber, userChannelType };
}
